"use client";

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { useAuth } from "../../../hooks/useAuth";
import { channelsAPI, handleApiError } from "../../../services/api";
import { getUserTypeColor } from "../../../utils/userTypeUtils";
import "./ChannelRulesModal.css";

const ChannelRulesModal = ({
  isOpen,
  onClose,
  channelId,
  initialRules = [],
  onRulesUpdated,
}) => {
  const { t, i18n } = useTranslation();
  const { getUserType } = useAuth();

  const currentUserType = getUserType();
  const userTypeColor = getUserTypeColor(currentUserType);

  const [draft, setDraft] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDraft((initialRules || []).join("\n"));
    }
  }, [isOpen, initialRules]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!channelId) return;

    const nextRules = draft
      .split(/\n+/)
      .map((r) => r.trim())
      .filter(Boolean)
      .slice(0, 6);

    setIsSubmitting(true);
    try {
      await channelsAPI.updateChannel(channelId, { rules: nextRules });

      if (onRulesUpdated) {
        onRulesUpdated(nextRules);
      }

      onClose();
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const modal = (
    <div
      className="channel-rules-modal-overlay"
      onClick={onClose}
      dir={i18n.language?.startsWith("ar") ? "rtl" : "ltr"}
    >
      <div
        className="channel-rules-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ "--user-type-color": userTypeColor }}
      >
        <div className="channel-rules-modal-header">
          <h2 className="channel-rules-modal-title">
            {t(
              "channelDetail.sidebar.rules.editTitle",
              "Community guidelines"
            )}
          </h2>
          <button
            type="button"
            className="channel-rules-modal-close"
            onClick={onClose}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="channel-rules-modal-form">
          <label className="channel-rules-label">
            {t(
              "channelDetail.sidebar.rules.editLabel",
              "Write your community rules and expectations (one per line):"
            )}
          </label>
          <textarea
            className="channel-rules-textarea"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={8}
            placeholder={t(
              "channelDetail.sidebar.rules.editPlaceholder",
              "Be kind and respectful\nNo spam or self-promotion\nStay on topic\n..."
            )}
          />

          <div className="channel-rules-modal-actions">
            <button
              type="button"
              className="channel-rules-cancel"
              onClick={onClose}
            >
              {t("common.cancel", "Cancel")}
            </button>
            <button
              type="submit"
              className="channel-rules-submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? t("common.saving", "Saving...")
                : t("common.save", "Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ChannelRulesModal;


